import type { ReactNode } from "react";
import { Icon } from "@/components/icons";
import { Tag, type TagTone } from "@/components/tag";
import { InfoHint } from "./info-hint";

export type StatTone = "neutral" | "negative";

type StatCardProps = {
  /** Material Symbols name for the leading icon. */
  icon?: string;
  /** Color treatment of the icon box. */
  iconTone?: StatTone;
  /** Optional pill in the top-right (e.g. "Quota", "Requires action"). */
  pill?: { label: string; tone: StatTone };
  /** Optional refresh button in the top-right (mutually exclusive with pill). */
  refresh?: boolean;
  label: string;
  /** Optional tooltip text shown via a circled "i" next to the label. */
  labelInfo?: string;
  value: string;
  /** Optional element rendered at the right of the value (e.g. a sparkline). */
  valueAccessory?: ReactNode;
  /** Optional content rendered below the value (e.g. quota bar). */
  extra?: ReactNode;
  /** Footer left content (text/link). */
  footerLeft?: ReactNode;
  /** Footer right content (link). */
  footerRight?: ReactNode;
};

const ICON_TONES: Record<StatTone, string> = {
  neutral: "bg-grey-100 text-grey-900",
  negative: "bg-red-100 text-red-600",
};

const STAT_TONE_TO_TAG_TONE: Record<StatTone, TagTone> = {
  neutral: "neutral",
  negative: "negative",
};

export function StatCard({
  icon,
  iconTone = "neutral",
  pill,
  refresh = false,
  label,
  labelInfo,
  value,
  valueAccessory,
  extra,
  footerLeft,
  footerRight,
}: StatCardProps) {
  return (
    <article className="card flex flex-col gap-3">
      <header className="flex items-start justify-between gap-3">
        {icon ? (
          <span
            className={`flex size-8 shrink-0 items-center justify-center rounded-[4px] ${ICON_TONES[iconTone]}`}
          >
            <Icon name={icon} size={20} />
          </span>
        ) : (
          <LabelRow label={label} info={labelInfo} />
        )}
        {pill && <Tag tone={STAT_TONE_TO_TAG_TONE[pill.tone]}>{pill.label}</Tag>}
        {refresh && (
          <button
            type="button"
            aria-label={`Refresh ${label}`}
            className="flex size-6 items-center justify-center rounded-[4px] text-grey-500 transition-colors hover:bg-grey-100 hover:text-grey-900"
          >
            <Icon name="refresh" size={20} />
          </button>
        )}
      </header>

      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-h1 leading-none text-black">{value}</p>
          {icon && <LabelRow label={label} info={labelInfo} />}
        </div>
        {valueAccessory}
      </div>

      {extra}

      {(footerLeft || footerRight) && (
        <>
          <div aria-hidden className="h-px w-full bg-grey-200" />
          <div className="flex items-center justify-between gap-3">
            {footerLeft ?? <span />}
            {footerRight}
          </div>
        </>
      )}
    </article>
  );
}

function LabelRow({ label, info }: { label: string; info?: string }) {
  if (!info) return <p className="text-small text-grey-900">{label}</p>;
  return (
    <div className="flex items-center gap-1 text-grey-900">
      <p className="text-small">{label}</p>
      <InfoHint text={info} />
    </div>
  );
}
